/**
 * How the interface builds its elements (decision 27: SolidJS; decision 44: Trusted Types). Solid's compiled JSX
 * clones HTML templates it writes as strings, which the CSP's `trusted-types 'none'` refuses, so the interface uses
 * Solid's hyperscript instead: every node is made with `createElement` and kept up to date by Solid's signals. This
 * module types the few calls the interface makes.
 */
import { createRoot, For, Index, Show, untrack } from 'solid-js';
import type { Accessor, JSX } from 'solid-js';
import { insert } from 'solid-js/web';
import h from 'solid-js/h';

/**
 * What an element can hold: nodes, text, nothing, lists, or a function Solid keeps the content up to date from; and
 * whatever a Solid component returns (PLAN.md §0: the library calls Solid's own), which Solid's `insert` takes too.
 */
export type Child = Node | string | number | null | undefined | false | readonly Child[] | (() => Child) | JSX.Element;

/** An element's attributes, properties, `on…` event handlers and `ref`; a function of no arguments stays up to date. */
export type Attributes = Readonly<Record<string, unknown>>;

/** An event from a handler on an element of type `T`, which is its `currentTarget` (Solid binds handlers to the element). */
export type On<E extends Event, T extends Element> = E & { readonly currentTarget: T };

/**
 * An element made by the DOM API (never from HTML text), with its attributes and children. It is made at once, so the
 * computations keeping it up to date belong to the component or list item making it: hyperscript's own lazy element
 * would be made later by whatever inserts it, and its computations die the next time that insert runs again.
 */
export function el(tag: string, attributes: Attributes = {}, ...children: readonly Child[]): Child {
  return h(tag, attributes, ...children)();
}

/** Runs a component once, untracked, as Solid's own components run, so reading a signal in its body subscribes nothing. */
export function view<P>(component: (props: P) => Child, props: P): Child {
  return untrack(() => component(props));
}

/** One child per item of `each`, kept by item as the list changes (Solid's `For`). */
export function each<T>(items: () => readonly T[], render: (item: T, index: () => number) => Child): Child {
  // Solid's hyperscript reads a child function of no parameters as a value to follow, so `render` is always given one
  // that takes its arguments (PLAN.md §0: a callback that ignores them, `() => …`, would be called with none).
  return h(For, { each: items }, (item: T, index: () => number) => render(item, index))();
}

/**
 * Part 11d: one child per place of `each`, kept by place (Solid's `Index`): the item at a place changes under its child,
 * which follows it, so a child (and the focus in it) stays as the list is read again.
 */
export function indexed<T>(items: () => readonly T[], render: (item: Accessor<T>) => Child): Child {
  // As `each`: a callback that takes its argument, whatever `render` declares.
  return h(Index, { each: items }, (item: Accessor<T>) => render(item))();
}

/**
 * Part 11d: `child` while `value` is something, given it as an accessor that is never null (Solid's `Show`), else
 * `fallback`; made again only when it turns to nothing and back, the accessor following the value meanwhile.
 */
export function whenSome<T>(value: () => T | null | undefined, child: (value: Accessor<T>) => Child, fallback: () => Child = () => null): Child {
  return h(Show, { when: value, fallback: () => untrack(fallback) }, (some: Accessor<T>) => child(some))();
}

/** `child` while `when` holds, else `fallback` (Solid's `Show`); both are made again each time `when` turns. */
export function when(test: () => boolean, child: () => Child, fallback: () => Child = () => null): Child {
  // Untracked, as a component runs: Show inserts a function child as an expression it reruns, so a signal the child read
  // while being made would make it again (and lose its state and focus) each time that signal changed.
  return h(Show, { when: test, fallback: () => untrack(fallback) }, () => untrack(child))();
}

/** Shows `child` as the content of `root` (Solid's `render`, typed for {@link Child}); returns what removes it. */
export function mount(child: () => Child, root: HTMLElement): () => void {
  const dispose = createRoot((done) => {
    root.replaceChildren();
    insert(root, child);
    return done;
  });
  return () => {
    dispose();
    root.replaceChildren();
  };
}
